// apps/admin/src/routes/licenses/LicensesList.tsx
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Key, AlertTriangle, ShieldAlert, MoreHorizontal, Eye, Pencil, RefreshCw, CheckCircle, Clock, XCircle, Play, Ban } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useDateFormat } from "../../hooks/useDateFormat";
import { SuperAdminGuard } from "../../components/SuperAdminGuard";
import {
  Button,
  Badge,
  Skeleton,
  Card,
  CardContent,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  useToast,
} from "@jourfx/ui";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge, type StatusType } from "../../components/StatusBadge";
import { EmptyState } from "../../components/EmptyState";

// License type from Convex query (JWT-based licensing schema)
interface License {
  _id: string;
  v?: number;
  kind: string;
  customerId: string;
  customerName: string;
  tier?: string;
  modules?: string[];
  status: string;
  validFrom: number;
  validUntil: number;
  isExpiring?: boolean;
  isExpired?: boolean;
}

function LicensesListContent() {
  const { t } = useTranslation('licenses');
  const { t: tc } = useTranslation('common');
  const { formatShortDate } = useDateFormat();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [token, setToken] = useState("");
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const extendLicense = useMutation(api.admin.licenses.extend);
  const suspendLicense = useMutation(api.admin.licenses.suspend);
  const reactivateLicense = useMutation(api.admin.licenses.reactivate);
  const loadLicense = useAction(api.licenseV2.loadLicense.loadLicense);

  const focusLoadTokenInput = (e: React.MouseEvent) => {
    e.stopPropagation();

    const input = document.getElementById("license-token-input") as HTMLInputElement | null;
    input?.scrollIntoView({ behavior: "smooth", block: "center" });
    input?.focus();
  };

  const getLoadLicenseErrorMessage = (error: unknown) => {
    const rawMessage =
      error instanceof Error ? error.message : typeof error === "string" ? error : "";

    const directMessage = rawMessage.match(/Invalid license:[^\n]+/i)?.[0];
    if (directMessage) {
      return directMessage;
    }

    return t("load.errorBody", {
      defaultValue:
        "The license token could not be loaded. Check that the token is signed for this installation.",
    });
  };

  const handleEditClick = (licenseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to detail page for editing
    navigate({ to: "/licenses/$licenseId", params: { licenseId } });
  };

  const handleExtendClick = async (_licenseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await extendLicense({ id: _licenseId as any, days: 30 });
      toast({
        title: t("actions.extend", "Lizenz verlängert"),
        description: t("actions.extendSuccess", "Die Lizenz wurde um 30 Tage verlängert."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    }
  };

  const handleSuspendClick = async (_licenseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await suspendLicense({ id: _licenseId as any });
      toast({
        title: t("actions.suspend", "Lizenz ausgesetzt"),
        description: t("actions.suspendSuccess", "Die Lizenz wurde ausgesetzt."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    }
  };

  const handleReactivateClick = async (_licenseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await reactivateLicense({ id: _licenseId as any });
      toast({
        title: t("actions.reactivate", "Lizenz reaktiviert"),
        description: t("actions.reactivateSuccess", "Die Lizenz ist wieder aktiv."),
      });
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    }
  };

  const licenses = useQuery(api.admin.licenses.list, {
    status: statusFilter !== "all" ? statusFilter as "active" | "trial" | "suspended" | "expired" | "revoked" : undefined,
  });

  if (licenses === undefined) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="grid gap-4 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-10 w-40" />
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (licenses === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc('auth.loginRequired')}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc('auth.loginRequiredMessage')}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {tc('auth.toLogin')}
        </Link>
      </div>
    );
  }

  const now = Date.now();
  const thirtyDays = 30 * 24 * 60 * 60 * 1000;
  const licenseList = (licenses || []) as License[];

  // Stats
  const activeLicenses = licenseList.filter((l) => l.status === "active").length;
  const trialLicenses = licenseList.filter((l) => l.status === "trial").length;
  const expiringLicenses = licenseList.filter(
    (l) => l.status === "active" && l.validUntil > now && l.validUntil - now < thirtyDays
  ).length;
  const expiredLicenses = licenseList.filter(
    (l) => l.validUntil < now || l.status === "expired"
  ).length;
  const hasV2Licenses = licenseList.some((license) => license.v === 2);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
      />

      {/* Load license (v2) */}
      <SuperAdminGuard>
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-foreground">
                  {t("load.title", "Load or replace license token")}
                </div>
                <div className="text-sm text-muted-foreground">
                  {t(
                    "load.subtitle",
                    "Paste a signed JWT license (v2) to activate it or replace an existing token for this installation."
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input
                id="license-token-input"
                value={token}
                onChange={(e) => {
                  setToken(e.target.value);
                  if (loadError) {
                    setLoadError(null);
                  }
                }}
                placeholder={t("load.placeholder", "eyJhbGciOi...")}
              />
              <Button
                onClick={async () => {
                  if (!token.trim()) return;
                  setIsLoadingToken(true);
                  setLoadError(null);
                  try {
                    await loadLicense({ token: token.trim() });
                    toast({
                      title: t("load.successTitle", "License loaded"),
                      description: t("load.successBody", "The license is now active."),
                    });
                    setToken("");
                  } catch (error) {
                    const message = getLoadLicenseErrorMessage(error);
                    setLoadError(message);
                    toast({
                      title: tc("auth.errorOccurred", "Error"),
                      description: message,
                      variant: "destructive",
                    });
                  } finally {
                    setIsLoadingToken(false);
                  }
                }}
                disabled={isLoadingToken || !token.trim()}
              >
                {isLoadingToken ? t("load.loading", "Loading...") : t("load.cta", "Load")}
              </Button>
            </div>
            {loadError && (
              <p role="alert" className="text-sm text-destructive">
                {loadError}
              </p>
            )}
            {!hasV2Licenses && (
              <p className="text-sm text-muted-foreground">
                {t(
                  "load.guidance",
                  "No active v2 license is loaded yet. Load a signed token to start or replace the current installation license."
                )}
              </p>
            )}
          </CardContent>
        </Card>
      </SuperAdminGuard>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <StatCard
          label={t('stats.active')}
          value={activeLicenses}
          icon={CheckCircle}
          iconColor="text-success"
          bgColor="bg-success-muted"
        />
        <StatCard
          label={t('stats.trial')}
          value={trialLicenses}
          icon={Play}
          iconColor="text-info"
          bgColor="bg-info-muted"
        />
        <StatCard
          label={t('stats.expiring')}
          value={expiringLicenses}
          icon={Clock}
          iconColor="text-warning"
          bgColor="bg-warning-muted"
        />
        <StatCard
          label={t('stats.expired')}
          value={expiredLicenses}
          icon={XCircle}
          iconColor="text-error"
          bgColor="bg-error-muted"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="all">{tc('table.allStatuses')}</option>
          <option value="active">{tc('status.active')}</option>
          <option value="trial">{tc('status.trial')}</option>
          <option value="suspended">{tc('status.suspended')}</option>
          <option value="expired">{tc('status.expired')}</option>
          <option value="revoked">{tc('status.revoked')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.organization')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.tier')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.validUntil')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.status')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tc('table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {licenseList.map((license) => {
              const isV2 = license.v === 2;
              const isExpiring = license.isExpiring || (
                license.status === "active" &&
                license.validUntil > now &&
                license.validUntil - now < thirtyDays
              );
              const isExpired = license.isExpired || license.validUntil < now;

              return (
                <tr
                  key={license._id}
                  className="group transition-colors hover:bg-muted/50 cursor-pointer"
                  onClick={() => navigate({ to: "/licenses/$licenseId", params: { licenseId: license._id } })}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <Key className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">
                          {license.customerName || t('table.unknown')}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {license.customerId}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge
                      variant={
                        license.tier === "enterprise"
                          ? "default"
                          : license.tier === "professional"
                          ? "secondary"
                          : "outline"
                      }
                      className={
                        license.tier === "enterprise"
                          ? "bg-primary/10 text-primary hover:bg-primary/10"
                          : license.tier === "professional"
                          ? "bg-info-muted text-info-muted-foreground hover:bg-info-muted"
                          : ""
                      }
                    >
                      {license.tier}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={isExpired ? "text-error font-medium" : isExpiring ? "text-warning font-medium" : "text-foreground"}>
                        {formatShortDate(license.validUntil)}
                      </span>
                      {(isExpiring || isExpired) && (
                        <AlertTriangle className={`h-4 w-4 ${isExpired ? "text-error" : "text-warning"}`} />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={license.status as StatusType} />
                  </td>
                  <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">{tc('table.openMenu')}</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/licenses/$licenseId" params={{ licenseId: license._id }}>
                            <Eye className="mr-2 h-4 w-4" />
                            {tc('actions.view')}
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => handleEditClick(license._id, e)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {tc('actions.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {isV2 ? (
                          <DropdownMenuItem onClick={focusLoadTokenInput}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {t("actions.replaceToken", "Load replacement token")}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={(e) => handleExtendClick(license._id, e)}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {t("actions.extend", "Verlängern")}
                          </DropdownMenuItem>
                        )}
                        {license.status === "active" && (
                          <DropdownMenuItem onClick={(e) => handleSuspendClick(license._id, e)}>
                            <Ban className="mr-2 h-4 w-4" />
                            {t("actions.suspend", "Aussetzen")}
                          </DropdownMenuItem>
                        )}
                        {(license.status === "suspended" || license.status === "revoked") && (
                          <DropdownMenuItem onClick={(e) => handleReactivateClick(license._id, e)}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {t("actions.reactivate", "Reaktivieren")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {licenseList.length === 0 && (
        <EmptyState
          icon={Key}
          title={
            statusFilter === "all"
              ? t("empty.v2Title", "No v2 licenses loaded")
              : t("empty.title")
          }
          description={
            statusFilter === "all"
              ? t(
                  "empty.v2Message",
                  "Load a signed replacement token to activate licensing for this installation."
                )
              : t("empty.message")
          }
        />
      )}
    </div>
  );
}

export function LicensesList() {
  return (
    <SuperAdminGuard>
      <LicensesListContent />
    </SuperAdminGuard>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  iconColor,
  bgColor,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  bgColor: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold tabular-nums text-foreground">{value}</p>
            <p className="text-sm text-muted-foreground">{label}</p>
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bgColor}`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
